import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { compose, withProps, withPropsOnChange, withState } from 'recompose';
import { formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import pick from 'lodash/pick';
import Profile from '../components/Profile';
import Notifications from '../components/Notifications';
import Appointments from '../components/Appointments';
import TabbedForm from '../../../../common/TabbedForm';
import CancelButtonLink from '../../../../common/buttons/cancelButtonLink';
import { trimFunction } from '../../../../common/trimData';
import { prepareMinsForSaving } from '../../../../common/helpers/schedules';
import { FORM } from '../constants';
import { validate } from '../helpers';
import LocateDuplicatesButton from '../../duplicateRecords/LocateDuplicates/LocateDuplicatesButton';
import withPermissions from '../../../../auth/utils/withPermissions';
import { canUserViewAppointmentContactInfo } from '../../../../common/helpers/users';
import { maskEmail, maskPhoneNumber } from '../../../../common/helpers/mask';
import CustomerMembership from '../memberships';
import withActiveUserIdAndType from '../../../../common/hoc/withActiveUserIdAndType';
import { canActiveShopUseMemberships, canActiveBrandUseMemberships } from '../../../../auth/selectors';

const fields = [
  'avatar',
  'firstName',
  'lastName',
  'email',
  'phone',
  'birthDate',
  'notifications',
  'notes',
  'blockNoShowFromBookNoPay',
  'unsubscribedCampaign',
  'leadId',
  'isBlocked',
];

class CustomerForm extends Component {
  static propTypes = {
    saveCustomer: PropTypes.func.isRequired,
    className: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
    deleteCustomer: PropTypes.func.isRequired,
    disabled: PropTypes.bool.isRequired,
    redirectUrl: PropTypes.string,
    initialValues: PropTypes.object.isRequired,
    edit: PropTypes.bool,
    activeId: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
    activeType: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
    owner: PropTypes.bool.isRequired,
    permissions: PropTypes.object.isRequired,
    email: PropTypes.string,
  }

  static defaultProps = {
    deleteCustomer: () => null,
    redirectUrl: '',
    customerId: '',
    timezone: '',
    edit: false,
    email: '',
  }

  saveCustomer = (data) => {
    let model = pick(data, fields);
    model = this.unmaskContactInfo(model);
    model.notifications.reminder.time = prepareMinsForSaving(model.notifications.reminder.time);
    model.notifications.barber_reminder.time = prepareMinsForSaving(model.notifications.barber_reminder.time);
    return this.props.saveCustomer(model);
  }

  unmaskContactInfo(data) {
    let { email } = data;
    if (!canUserViewAppointmentContactInfo(this.props.permissions)) {
      if (email === this.props.email) {
        email = this.props.initialValues.email;
      }
    }

    return { ...data, email };
  }

  renderDeleteButton = () => (
    <button
      type="button"
      className="c-btn c-btn--red u-width-120 u-mr- u-float-left"
      onClick={this.props.deleteCustomer}
    >
      <span className="c-btn__text">Delete</span>
    </button>
  );

  renderCancelButton = () => <CancelButtonLink link={this.props.redirectUrl} content="Cancel" />;

  renderSaveButton = () => (
    <button disabled={this.props.disabled} type="submit" className="c-btn u-width-120 pointer-events">
      <span className="c-btn__text">Save</span>
    </button>
  );

  renderLocateDuplicatesButton = () => <LocateDuplicatesButton customerId={this.props.initialValues.id} />

  render() {
    let { initialValues } = this.props;
    initialValues = { ...initialValues, email: this.props.email };

    return (
      <TabbedForm
        className="u-max-width-1000 u-color-white u-margin-auto"
        form={FORM}
        initialValues={initialValues}
        validate={validate}
        onSubmit={trimFunction(this.saveCustomer)}
        cancelButton={this.renderCancelButton}
        deleteButton={this.renderDeleteButton}
        saveButton={this.renderSaveButton}
        edit={this.props.edit}
        TabBarAction={this.renderLocateDuplicatesButton}
      >
        <TabbedForm.Tab title="Profile">
          <Profile
            {...this.props}
          />
        </TabbedForm.Tab>
        <TabbedForm.Tab title="Notifications">
          <Notifications
            {...this.props}
            disabled={Boolean(this.props.owner)}
          />
        </TabbedForm.Tab>
        <TabbedForm.Tab title="Appointments" display-if={this.props.edit}>
          <Appointments {...this.props} />
        </TabbedForm.Tab>
        <TabbedForm.Tab title="Membership" display-if={this.props.canSeeMemberships}>
          <CustomerMembership {...this.props} />
        </TabbedForm.Tab>
      </TabbedForm>
    );
  }
}

const selector = formValueSelector(FORM);

export default compose(
  withPermissions,
  withActiveUserIdAndType(),
  connect((state, props) => {
    // barbers can't see customer memberships
    let canSeeMemberships = false;

    if (props.activeType === 'brand') {
      canSeeMemberships = !!props.edit && canActiveBrandUseMemberships(state);
    }

    if (props.activeType === 'shop') {
      canSeeMemberships = !!props.edit && canActiveShopUseMemberships(state);
    }

    return {
      canSeeMemberships,
      phone: selector(state, 'phone'),
    };
  }),
  withProps(props => ({
    email: !canUserViewAppointmentContactInfo(props.permissions) ?
      maskEmail(props.initialValues.email) :
      props.initialValues.email,
    blockStatusCanBeChanged: props.initialValues.blockStatusCanBeChanged,
  })),
  withProps(props => ({
    leadAutoAssigned: props.initialValues.leadAutoAssigned || false,
  })),
  withState(
    'phoneFormatter',
    'setPhoneFormatter',
    props => (!canUserViewAppointmentContactInfo(props.permissions) ? maskPhoneNumber : null)
  ),
  withPropsOnChange(['phone'], (props) => {
    if (
      props.phoneFormatter !== null &&
      props.phone !== undefined &&
      props.phone !== props.initialValues.phone
    ) {
      props.setPhoneFormatter(null);
    }
  })
)(CustomerForm);
